import { FormEvent, SyntheticEvent, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ColorResult, SketchPicker } from 'react-color';
import BootstrapForm from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import pick from 'lodash/pick';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { extractFacetExpression } from 'common/util/graph';

import Tab from 'components/common/Tab/Tab';
import Tabs from 'components/common/Tabs/Tabs';
import TabList from 'components/common/TabList/TabList';
import TabContents from 'components/common/TabContents/TabContents';
import TabContent from 'components/common/TabContent/TabContent';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import Dialog from 'components/common/Dialog/Dialog';
import CodeEditor from 'components/common/CodeEditor/CodeEditor';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertConfig, PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import ComboBox from 'components/common/ComboBox/ComboBox';
import OverlayTrigger from 'components/common/OverlayTrigger/OverlayTrigger';
import { facetListState } from 'components/search/facets.state';
import { useFacetsNeeded } from 'hooks/useFacetsNeeded';

import { GraphPlotConfigType } from 'components/dashboards/types';

import { plotsStylesState } from 'styles/stylesProvider';
import './AddEditPlotsModal.scss';

const TAB_FACET = 'facet';
const TAB_JSON = 'json';
const RATE_FACET = 'rate';
const WHERE_KEY_WORD = 'where';
const BASIC_FUNCTION_FIELDS = ['filter', 'facet', 'color', 'label'];

const getAlertConfig = (message: string | JSX.Element): PageAlertConfig => ({
  message,
  variant: PageAlertVariant.WARNING,
  autoHide: true,
  duration: 5,
});

const INVALID_FIELD_MESSAGE = (
  <>
    To be able to edit using the Basic Function tab,
    <br />
    {'any property specified in the json below must be one of the following: '}
    <br />
    {BASIC_FUNCTION_FIELDS.join(', ')}
  </>
);

const INVALID_FACET_MESSAGE = (
  <>
    To be able to edit using the Basic Function tab,
    <br />
    {"the facet function used in the 'facet' attribute must be one of the following: "}
    <br />
    {K.FACET_FUNCTIONS.reduce((a, b) => a + b.op + ' ', '')}
  </>
);

const INVALID_EDIT_MESSAGE = (
  <>
    JSON could not be translated to a basic function!
    <br />
    In order to edit using the basic function tab, create a valid facet graph using the json below.
  </>
);
const INVALID_JSON_MESSAGE = 'Invalid JSON format';

const INVALID_FIELD_CONFIG = getAlertConfig(INVALID_FIELD_MESSAGE);
const INVALID_FACET_CONFIG = getAlertConfig(INVALID_FACET_MESSAGE);
const INVALID_JSON_CONFIG = getAlertConfig(INVALID_JSON_MESSAGE);
const INVALID_EDIT_CONFIG = getAlertConfig(INVALID_EDIT_MESSAGE);

type BasicFunctionPlot = {
  facet?: string;
  facetVariable?: string;
  facetFunction?: string;
  filter?: string;
  label?: string;
  color?: string;
};

type AddEditPlotsModalProps = {
  showModal?: boolean;
  isEdit: boolean;
  plotIndex: number;
  plot: GraphPlotConfigType;
  plotModalSubmitCallback: (plotModel: GraphPlotConfigType) => void;
  handleClose: () => void;
};

const extendPlotForModal = (plot: GraphPlotConfigType): BasicFunctionPlot => {
  const { facetFunction, facetVariable } = extractFacetExpression(plot.facet);
  const basicFunctionPlot = { ...plot, facetFunction: facetFunction || 'rate', facetVariable };
  return basicFunctionPlot;
};

const generatePlotForJson = (plotIn: BasicFunctionPlot): GraphPlotConfigType => {
  const plot = { ...plotIn };
  if (plot.facetVariable && plot.facetFunction && plot.facetFunction !== 'count') {
    plot.facet = `${plot.facetFunction}(${plot.facetVariable})`;
  } else {
    plot.facet = RATE_FACET;
  }
  const plotForJson = pick(plot, BASIC_FUNCTION_FIELDS);
  return plotForJson;
};

const generateEditorJsonString = (plot: BasicFunctionPlot): string => {
  const editorJson = generatePlotForJson(plot);
  return JSON.stringify(editorJson, null, '\t');
};

/**
 * Add Graph plot component is used to add/edit dashboard graph plots.
 *
 * @prop showModal                modal is shown when true
 * @prop isEdit                   if true, plot is being edited rather than added
 * @prop plotIndex                index of plot within its graph
 * @prop plot                     plot model values. This parameter is used when editing an existing plot
 * @prop plotModalSubmitCallback  plot modal submit callback function
 * @prop handleClose              Callback for when the model is closed.
 */

function AddEditPlotsModal({
  showModal = false,
  isEdit,
  plot,
  plotIndex,
  plotModalSubmitCallback,
  handleClose,
}: AddEditPlotsModalProps): JSX.Element {

  const [plotModel, setPlotModel] = useState<BasicFunctionPlot>(extendPlotForModal(plot));
  const [codeValue, setCodeValue] = useState<string>(generateEditorJsonString(plot));
  const [validated, setValidated] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_FACET);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const plotsStyles = useRecoilValue(plotsStylesState);

  // make sure we have facets
  const facetList = useRecoilValue(facetListState);
  useFacetsNeeded();

  // Handle a facet function selection. Also change label and facet to the selected one.
  const handleFunctionSelect = (facetFn: string): void => {
    if (facetFn !== plotModel.facetFunction) {
      const updatedPlotsModel = {
        ...plotModel,
        label: K.FACET_FUNCTIONS.find((facet) => facet.op === facetFn)?.name || '',
        facetFunction: facetFn,
      };
      setPlotModel(updatedPlotsModel);
    }
  };

  // Validate the plot first and do some cleanup of the plot before sending back to caller.
  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      setValidated(true);
    } else {
      const plotToBeSaved = { ...plotModel };
      if (plotToBeSaved.facetFunction === RATE_FACET) {
        plotToBeSaved.facetVariable = '';
      }
      plotModalSubmitCallback(generatePlotForJson(plotToBeSaved));
      handleClose();
    }
  };

  const handleFormChange = (event: SyntheticEvent): void => {
    const eventTarget = event.target as HTMLInputElement;
    const updatedPlotsModel = { ...plotModel, [eventTarget.name]: eventTarget.value };
    setPlotModel(updatedPlotsModel);
  };

  // The facet function selection list
  const functionsList = K.FACET_FUNCTIONS.map((func) => (
    <div
      className={
        'AddEditPlotsModal__Function ' +
        (plotModel.facetFunction === func.op || plotModel.facetFunction === func.name
          ? 'AddEditPlotsModal__Function--Selected'
          : '')
      }
      key={func.op}
      onClick={(): void => {
        handleFunctionSelect(func.op);
      }}
      role="presentation"
      data-test-id={createTestId(ELEMENT.dashboardAddPlotFunction, func.op)}
    >
      {func.name}
    </div>
  ));

  // Get facet description based on current selected facet function
  const getFacetFuncDescription = (): string => {
    if (plotModel.facet) {
      return K.FACET_FUNCTIONS.find((func) => func.op === plotModel.facetFunction)?.description || '';
    }
    return '';
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onCodeChange = (editor: any, _data: any, value: string): void => {
    setCodeValue(value);
  };

  // check to see if the facet function from Json is supported
  const isSupportedFacetFunction = (facetFunction: string): boolean =>
    K.FACET_FUNCTIONS.some((facet) => facet.op === facetFunction);

  // A helpful function to return a new plot modal based on the input and do trimming.
  const plotForBasicFnTabHelper = (
    facetFunction: string | undefined,
    facetVariable: string | undefined,
    filter: string | undefined,
  ): GraphPlotConfigType => {
    const newPlot = {} as BasicFunctionPlot;
    newPlot.facetFunction = facetFunction?.trim();
    newPlot.facetVariable = facetVariable?.trim();
    newPlot.filter = filter?.trim();

    return newPlot;
  };

  // Handle switch tabs. Main purpose is to make the two tabs synced.
  // The user can only switch back to Basic function if it is a valid json.
  const handleTabSelect = (eventKey: string): undefined => {
    // clicking on the same tab does nothing
    if (eventKey === activeTab) {
      return;
    }

    // Basic Function tab -> Json tab
    if (eventKey === TAB_JSON) {
      setCodeValue(generateEditorJsonString(plotModel));
      setActiveTab(eventKey);
      return;
    }

    // validation
    let parsedPlotModel = {} as GraphPlotConfigType;
    try {
      // first try to parse JSON to make sure the format is correct
      parsedPlotModel = JSON.parse(codeValue);
    } catch (e) {
      setPageAlertConfig(INVALID_JSON_CONFIG);
      return;
    }

    // Checks to make sure all of the keys specified in the plotJson apply to the basic function tab
    const fields = Object.keys(parsedPlotModel);
    if (fields.some((field) => !BASIC_FUNCTION_FIELDS.includes(field))) {
      setPageAlertConfig(INVALID_FIELD_CONFIG);
      return;
    }

    /** the real deal begins * */
    const { filter: jsonUpdatedFilter, facet: jsonUpdatedFacet } = parsedPlotModel;

    // Try and split the filter and facet attribute with () braces.
    // ex. "sumPerSec(agentV2)" =>  ["sumPerSec", "agentV2"]
    const { facetFunction, facetVariable } = extractFacetExpression(jsonUpdatedFacet);
    const { facetFunction: filterFunction, facetVariable: filterVariable } = extractFacetExpression(jsonUpdatedFilter);

    if (jsonUpdatedFacet !== RATE_FACET) {
      // we are done if facet exists but not supported
      if (facetFunction && !isSupportedFacetFunction(facetFunction)) {
        setPageAlertConfig(INVALID_FACET_CONFIG);
        return;
      }

      // if it was a malformed facet, ex. facet(field foo)
      if (facetVariable && facetVariable.includes(' ')) {
        setPageAlertConfig(INVALID_EDIT_CONFIG);
        return;
      }
    }

    let newPlotModel = {};
    if (jsonUpdatedFacet === RATE_FACET) {
      // a special case: It's a rate plot.
      // We just need to move the "filter" attribute over and mark the facetFunction as "count"
      newPlotModel = plotForBasicFnTabHelper(RATE_FACET, '', jsonUpdatedFilter);
    } else if (facetFunction && facetVariable) {
      // case: The format was valid: facet(field).
      newPlotModel = plotForBasicFnTabHelper(facetFunction, facetVariable, jsonUpdatedFilter);
    } else if (filterFunction && filterVariable) {
      // case: There was no facet field in JSON so try get the facet and field in filer.

      // Split the expression inside the braces by 'where'
      // ex. 'ageSecs where ageSecs >= 2 where sum >= 2' => ['ageSecs ', ' ageSec >= 2 ', ' sum >= 2']
      const functionParams = filterVariable.split(WHERE_KEY_WORD);

      if (functionParams.length > 0 && isSupportedFacetFunction(filterFunction)) {
        newPlotModel = plotForBasicFnTabHelper(
          functionParams.shift(),
          filterFunction,
          functionParams.join(WHERE_KEY_WORD),
        );
      } else {
        setPageAlertConfig(INVALID_FACET_CONFIG);
        return;
      }
    } else {
      // anything else is an error. ex. a non-rate facet without a field, or no facet and no filter
      setPageAlertConfig(INVALID_EDIT_CONFIG);
      return;
    }
    setPlotModel({ ...parsedPlotModel, ...newPlotModel });
    setActiveTab(eventKey);
  };

  const handleComboBoxFacetChange = (facetVariable: string): void => {
    const updatedPlotsModel = { ...plotModel, facetVariable } as BasicFunctionPlot;
    setPlotModel(updatedPlotsModel);
  };

  const dialogFooter = {
    rightItems: [
      <Button variant={ButtonVariant.PRIMARY} testId={ELEMENT.dashboardAddPlotOk} type="submit" submitForm={K.DIALOG_FORM_IDS.ADD_EDIT_PLOTS_MODAL_FORM_ID}>
        OK
      </Button>,
      <Button variant={ButtonVariant.SECONDARY} testId={ELEMENT.dashboardAddPlotCancel} onClick={handleClose}>
        Cancel
      </Button>,
    ],
  };

  const toggleColorPicker = (): void => setShowColorPicker(!showColorPicker);
  const useColorFromPicker = (color: ColorResult): void => {
    const updatedPlotsModel = { ...plotModel, color: color.hex };
    setPlotModel(updatedPlotsModel);
  };
  const displayColor = plotModel.color ? plotModel.color : plotsStyles[plotIndex];
  const colorPicker = <SketchPicker color={displayColor} onChange={useColorFromPicker} presetColors={plotsStyles} />;

  return (
    <Dialog
      title={isEdit ? 'Edit Plot' : 'Add Plot'}
      show={showModal}
      close={handleClose}
      footer={dialogFooter}
      noAutoFocus={plotModel.facetFunction === RATE_FACET}
      scrollable={true}
    >
      <div className="AddEditPlotsModal">
        <Tabs
          id="AddEditPlotsModal"
          activeTab={activeTab}
          onSelect={(eventKey): void => handleTabSelect(eventKey)}
          mountOnEnter={true}
        >
          <TabList className={activeTab === TAB_FACET ? 'TabList__Tabs' : ''}>
            <Tab eventKey={TAB_FACET} testId={ELEMENT.dashboardAddPlotBasicTab}>
              Basic Function
            </Tab>
            <Tab eventKey={TAB_JSON} testId={ELEMENT.dashboardAddPlotJsonTab}>
              JSON Editor
            </Tab>
          </TabList>
          <TabContents>
            <TabContent eventKey={TAB_FACET}>
              <BootstrapForm noValidate validated={validated} onSubmit={handleFormSubmit} id={K.DIALOG_FORM_IDS.ADD_EDIT_PLOTS_MODAL_FORM_ID}>
                {/* Label row: Function, Field, Facet Function Description */}
                <BootstrapForm.Row className="AddEditPlotsModal__InputRow">
                  <Col xs={6}>
                    <BootstrapForm.Label>Function</BootstrapForm.Label>
                    <div className="AddEditPlotsModal__FunctionsList">{functionsList}</div>
                  </Col>
                  <Col xs={6}>
                    <BootstrapForm.Label>Field</BootstrapForm.Label>
                    <ComboBox
                      className="AddEditPlotsModal__TopFacetComboBox"
                      value={plotModel.facetVariable || ''}
                      items={facetList}
                      onChange={handleComboBoxFacetChange}
                      disabled={plotModel.facetFunction === RATE_FACET}
                      required={plotModel.facetFunction !== RATE_FACET}
                      testId={ELEMENT.dashboardAddPlotField}
                    />
                    <div className="AddEditPlotsModal__FunctionDescription">{getFacetFuncDescription()}</div>
                  </Col>
                </BootstrapForm.Row>

                {/* middle row: Filter */}
                <BootstrapForm.Row className="AddEditPlotsModal__InputRow">
                  <BootstrapForm.Label column xs={1} className="AddEditPlotsModal__RowLabel">
                    Filter
                  </BootstrapForm.Label>
                  <Col xs={11}>
                    <BootstrapForm.Control
                      name="filter"
                      as="textarea"
                      value={plotModel.filter}
                      onChange={handleFormChange}
                      placeholder="Search filter text (optional)"
                      data-test-id={ELEMENT.dashboardAddPlotFilter}
                    />
                  </Col>
                </BootstrapForm.Row>

                {/* bottom row: Label, Color */}
                <BootstrapForm.Row className="AddEditPlotsModal__InputRow">
                  <BootstrapForm.Label column xs={1} className="AddEditPlotsModal__RowLabel">
                    Label
                  </BootstrapForm.Label>
                  <Col xs={4}>
                    <BootstrapForm.Control
                      name="label"
                      type="text"
                      value={plotModel.label}
                      onChange={handleFormChange}
                      placeholder="What to call this plot"
                      required
                      data-test-id={ELEMENT.dashboardAddPlotLabel}
                    />
                    <BootstrapForm.Control.Feedback type="invalid">Please enter a label</BootstrapForm.Control.Feedback>
                  </Col>
                  <Col xs={3} className="AddEditPlotsModal__RowLabel">
                    <span className="AddEditPlotsModal__ColorLabel">Color</span>
                    <span
                      role="presentation"
                      onClick={toggleColorPicker}
                      className="AddEditPlotsModal__ColorSwatch"
                      style={{ backgroundColor: displayColor }}
                      data-test-id={ELEMENT.dashboardAddPlotColorToggle}
                    />
                  </Col>
                  <Col xs={4}>
                    <OverlayTrigger
                      placement="bottom-end"
                      overlay={colorPicker}
                      showOverlay={showColorPicker}
                      setShowOverlay={setShowColorPicker}
                    >
                      <BootstrapForm.Control
                        name="color"
                        type="text"
                        value={plotModel.color}
                        onChange={handleFormChange}
                        maxLength={7}
                        autoComplete="off"
                        placeholder={plotsStyles[plotIndex]}
                        data-test-id={ELEMENT.dashboardAddPlotColor}
                      />
                    </OverlayTrigger>
                  </Col>
                </BootstrapForm.Row>
              </BootstrapForm>
            </TabContent>
            <TabContent eventKey={TAB_JSON}>
              <CodeEditor
                value={codeValue}
                onChange={onCodeChange}
                className="AddEditPlotsModal__CodeEditor"
                options={{ lineNumbers: true }}
              />
            </TabContent>
          </TabContents>
        </Tabs>
      </div>
    </Dialog>
  );
}

export default AddEditPlotsModal;
